/**
 * Employee Custody (العهد) — admin management page.
 *
 * One page covers the two use-cases a small HR team actually needs:
 *   • Inventory list (every physical asset the company owns), filterable
 *     by category + status + free-text search.
 *   • Handover + return workflow with digital signature + instant PDF.
 *
 * The sibling tab shows the custody ledger (who holds what, when it was
 * handed over, when returned). An admin can hit "استرجاع" from either
 * tab.
 *
 * Route: /admin/custody
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, Plus, MagnifyingGlass, CaretLeft, CheckCircle, Warning,
  SpinnerGap, FilePdf, ArrowCircleDown, ArrowCircleUp, Trash, Pencil, Lock,
  DeviceMobile, Laptop, IdentificationBadge, Stamp, TShirt, Key, Car,
  FirstAid, Wrench, DotsThree,
} from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import SignaturePad from '@/components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const CATEGORIES = [
  { key: 'id_card',         ar: 'بطاقة تعريف',   en: 'ID Card',        icon: IdentificationBadge },
  { key: 'phone',           ar: 'هاتف جوال',     en: 'Phone',          icon: DeviceMobile },
  { key: 'laptop',          ar: 'جهاز محمول',    en: 'Laptop',         icon: Laptop },
  { key: 'stamp',           ar: 'ختم',           en: 'Stamp',          icon: Stamp },
  { key: 'uniform',         ar: 'زي رسمي',       en: 'Uniform',        icon: TShirt },
  { key: 'key',             ar: 'مفتاح',         en: 'Key',            icon: Key },
  { key: 'car',             ar: 'سيارة',         en: 'Vehicle',        icon: Car },
  { key: 'medical_device',  ar: 'جهاز طبي',      en: 'Medical device', icon: FirstAid },
  { key: 'tool',            ar: 'أداة عمل',      en: 'Tool',           icon: Wrench },
  { key: 'other',           ar: 'أخرى',          en: 'Other',          icon: DotsThree },
];
const STATUSES = [
  { key: 'available',        ar: 'متاح',           en: 'Available',         color: 'emerald' },
  { key: 'pending_handover', ar: 'بانتظار التوقيع', en: 'Pending signature', color: 'amber-wait' },
  { key: 'assigned',         ar: 'مُسلَّمة',        en: 'Assigned',          color: 'violet' },
  { key: 'lost',             ar: 'مفقودة',          en: 'Lost',              color: 'red' },
  { key: 'damaged',          ar: 'تالفة',           en: 'Damaged',           color: 'amber' },
  { key: 'retired',          ar: 'خارج الخدمة',     en: 'Retired',           color: 'slate' },
];

const catMeta = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[CATEGORIES.length - 1];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];

const BADGE_CLASS = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  violet:  'bg-violet-100  text-violet-700  dark:bg-violet-900/30  dark:text-violet-300',
  red:     'bg-red-100     text-red-700     dark:bg-red-900/30     dark:text-red-300',
  amber:   'bg-amber-100   text-amber-700   dark:bg-amber-900/30   dark:text-amber-300',
  'amber-wait': 'bg-orange-100 text-orange-700 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700',
  slate:   'bg-slate-100   text-slate-700   dark:bg-slate-800      dark:text-slate-300',
};

export default function Custody() {
  const { language, direction } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const T = (a, e) => (isAr ? a : e);

  const [assets, setAssets] = useState([]);
  const [custody, setCustody] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ category: 'laptop', name: '', serial_number: '', brand: '', model: '', purchase_value: '' });
  const [savingAdd, setSavingAdd] = useState(false);

  // Edit asset
  const [editTarget, setEditTarget] = useState(null); // asset being edited
  const [editForm, setEditForm] = useState({ category: 'laptop', name: '', serial_number: '', brand: '', model: '', purchase_value: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [handoverTarget, setHandoverTarget] = useState(null); // asset
  const [handoverForm, setHandoverForm] = useState({ user_id: '', condition: 'good', notes: '', signature_name: '', request_employee_signature: true });
  const sigRefHandover = useRef(null);
  const [sigEmptyHandover, setSigEmptyHandover] = useState(true);
  const [savingHandover, setSavingHandover] = useState(false);

  const [returnTarget, setReturnTarget] = useState(null); // custody record
  const [returnForm, setReturnForm] = useState({ condition: 'good', status: 'returned', notes: '', signature_name: '' });
  const sigRefReturn = useRef(null);
  const [sigEmptyReturn, setSigEmptyReturn] = useState(true);
  const [savingReturn, setSavingReturn] = useState(false);

  // Delete-custody confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);   // custody record
  const [deleting, setDeleting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, c, e, s] = await Promise.all([
        axios.get(`${API}/assets`, { withCredentials: true }),
        axios.get(`${API}/custody`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/custody/stats`, { withCredentials: true }),
      ]);
      setAssets(a.data || []);
      setCustody(c.data || []);
      setEmployees(e.data || []);
      setStats(s.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل التحميل', 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const filteredAssets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (filterCategory !== 'all' && a.category !== filterCategory) return false;
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      if (!q) return true;
      return (
        (a.name || '').toLowerCase().includes(q)
        || (a.serial_number || '').toLowerCase().includes(q)
        || (a.brand || '').toLowerCase().includes(q)
        || (a.current_holder?.name || '').toLowerCase().includes(q)
      );
    });
  }, [assets, search, filterCategory, filterStatus]);

  const createAsset = async () => {
    if (!addForm.name || addForm.name.length < 2) {
      toast.error(T('اسم الصنف مطلوب', 'Name is required'));
      return;
    }
    setSavingAdd(true);
    try {
      await axios.post(`${API}/assets`, {
        ...addForm,
        purchase_value: parseFloat(addForm.purchase_value || 0) || 0,
      }, { withCredentials: true });
      toast.success(T('تم إضافة الصنف', 'Asset added'));
      setAddDialog(false);
      setAddForm({ category: 'laptop', name: '', serial_number: '', brand: '', model: '', purchase_value: '' });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingAdd(false);
    }
  };

  const openHandover = (asset) => {
    setHandoverTarget(asset);
    setHandoverForm({ user_id: '', condition: 'good', notes: '', signature_name: '', request_employee_signature: true });
    setSigEmptyHandover(true);
    setTimeout(() => sigRefHandover.current?.clear(), 50);
  };

  const submitHandover = async () => {
    if (!handoverTarget) return;
    if (!handoverForm.user_id) { toast.error(T('اختر الموظف المستلم', 'Pick the recipient')); return; }
    const requestOnly = handoverForm.request_employee_signature;
    if (!requestOnly && sigEmptyHandover) { toast.error(T('يرجى التوقيع أو تفعيل خيار "طلب توقيع من الموظف"', 'Draw a signature or enable "Request employee signature"')); return; }
    const sigImage = requestOnly ? null : sigRefHandover.current?.toDataURL();
    setSavingHandover(true);
    try {
      await axios.post(`${API}/assets/${handoverTarget.id}/handover`, {
        user_id: handoverForm.user_id,
        condition_at_handover: handoverForm.condition,
        notes: handoverForm.notes,
        signature_name: handoverForm.signature_name,
        signature_image: sigImage,
        request_employee_signature: requestOnly,
      }, { withCredentials: true });
      toast.success(requestOnly
        ? T('تم إرسال طلب التوقيع إلى الموظف', 'Signature request sent to employee')
        : T('تم التسليم بنجاح', 'Handed over successfully'));
      setHandoverTarget(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل التسليم', 'Handover failed'));
    } finally {
      setSavingHandover(false);
    }
  };

  const openReturn = (rec) => {
    setReturnTarget(rec);
    setReturnForm({ condition: 'good', status: 'returned', notes: '', signature_name: rec?.user?.name || '' });
    setSigEmptyReturn(true);
    setTimeout(() => sigRefReturn.current?.clear(), 50);
  };

  const submitReturn = async () => {
    if (!returnTarget) return;
    const sigImage = returnForm.status === 'returned' && !sigEmptyReturn
      ? sigRefReturn.current?.toDataURL()
      : null;
    if (returnForm.status === 'returned' && !sigImage) {
      toast.error(T('يرجى توقيع الموظف عند استلام الصنف', 'Signature required on return'));
      return;
    }
    setSavingReturn(true);
    try {
      await axios.post(`${API}/custody/${returnTarget.id}/return`, {
        condition_at_return: returnForm.condition,
        status: returnForm.status,
        notes: returnForm.notes,
        signature_name: returnForm.signature_name,
        signature_image: sigImage,
      }, { withCredentials: true });
      toast.success(T('تم الاسترجاع بنجاح', 'Returned successfully'));
      setReturnTarget(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل الاسترجاع', 'Return failed'));
    } finally {
      setSavingReturn(false);
    }
  };

  const openReceipt = (recordId, kind) => {
    window.open(`${API}/custody/${recordId}/receipt.pdf?kind=${kind}`, '_blank');
  };

  const deleteAsset = async (asset) => {
    if (!window.confirm(T(
      `هل تريد حذف الصنف "${asset.name}"؟ لا يمكن التراجع.`,
      `Delete asset "${asset.name}"? This cannot be undone.`,
    ))) return;
    try {
      await axios.delete(`${API}/assets/${asset.id}`, { withCredentials: true });
      toast.success(T('تم الحذف', 'Deleted'));
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل الحذف', 'Delete failed'));
    }
  };

  const openEditAsset = (asset) => {
    if (asset.status !== 'available') {
      toast.error(T('يمكن تعديل الأصناف المتاحة فقط', 'Only available assets can be edited'));
      return;
    }
    setEditTarget(asset);
    setEditForm({
      category: asset.category || 'laptop',
      name: asset.name || '',
      serial_number: asset.serial_number || '',
      brand: asset.brand || '',
      model: asset.model || '',
      purchase_value: asset.purchase_value ?? '',
      notes: asset.notes || '',
    });
  };

  const saveEditAsset = async () => {
    if (!editTarget) return;
    if (!editForm.name || editForm.name.trim().length < 2) {
      toast.error(T('اسم الصنف مطلوب', 'Asset name is required'));
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/assets/${editTarget.id}`, {
        category: editForm.category,
        name: editForm.name.trim(),
        serial_number: editForm.serial_number,
        brand: editForm.brand,
        model: editForm.model,
        purchase_value: editForm.purchase_value === '' || editForm.purchase_value === null ? null : Number(editForm.purchase_value),
        notes: editForm.notes,
      }, { withCredentials: true });
      toast.success(T('تم تحديث الصنف', 'Asset updated'));
      setEditTarget(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل التحديث', 'Update failed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDeleteCustody = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/custody/${deleteTarget.id}`, { withCredentials: true });
      toast.success(T('تم حذف سجل العهدة', 'Custody record deleted'));
      setDeleteTarget(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل الحذف', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="custody-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <Button variant="ghost" onClick={() => navigate('/home')} className="text-[#655dc6] hover:bg-[#EDE9FE]" data-testid="back-home-btn">
            <CaretLeft size={18} className={isAr ? 'ms-1 rotate-180' : 'me-1'} />
            {T('رجوع', 'Back')}
          </Button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title + add */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <Package size={28} weight="duotone" />
              {T('العهد والأصول', 'Assets & Custody')}
            </h1>
            <p className="text-sm text-[#655dc6]/70 dark:text-slate-400 mt-1">
              {T('سجل متكامل للعهد — من بطاقات التعريف إلى الأجهزة — مع توقيع رقمي وسندات PDF.', 'Full inventory of company assets — from ID cards to equipment — with digital signatures and PDF receipts.')}
            </p>
          </div>
          <Button onClick={() => setAddDialog(true)} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2 shrink-0" data-testid="add-asset-btn">
            <Plus size={18} weight="bold" />
            {T('إضافة صنف', 'Add Asset')}
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {[
            { k: 'total_assets',     ar: 'إجمالي الأصول',  en: 'Total',             color: 'violet',     icon: Package },
            { k: 'pending_handover', ar: 'بانتظار التوقيع', en: 'Pending signature', color: 'amber-wait', icon: Warning },
            { k: 'assigned',         ar: 'مُسلَّمة',        en: 'Assigned',          color: 'violet',     icon: ArrowCircleUp },
            { k: 'available',        ar: 'متاحة',           en: 'Available',         color: 'emerald',    icon: CheckCircle },
            { k: 'damaged',          ar: 'تالفة',            en: 'Damaged',           color: 'amber',      icon: Warning },
            { k: 'lost',             ar: 'مفقودة',          en: 'Lost',              color: 'red',        icon: Warning },
          ].map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.k} className="border-[#C4BFF0] dark:border-slate-700" data-testid={`kpi-${kpi.k}`}>
                <CardContent className="pt-4 pb-3 px-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${BADGE_CLASS[kpi.color]}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-[11px] text-[#655dc6]/60 dark:text-slate-400 uppercase tracking-wide">{T(kpi.ar, kpi.en)}</p>
                    <p className="text-xl font-bold text-[#655dc6] dark:text-violet-300">{stats[kpi.k] ?? 0}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main tabs */}
        <Tabs defaultValue="assets" dir={direction} className="w-full">
          <TabsList className="bg-white dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="assets" data-testid="tab-assets" className="shrink-0">{T('قائمة الأصول', 'Assets')}</TabsTrigger>
            <TabsTrigger value="custody" data-testid="tab-custody" className="shrink-0">{T('سجل العهد', 'Custody ledger')}</TabsTrigger>
          </TabsList>

          <TabsContent value="assets" className="mt-4">
            <Card className="border-[#C4BFF0] dark:border-slate-700">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg text-[#655dc6] dark:text-violet-300">{T('الأصول', 'Assets')}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                      <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 start-2 text-[#655dc6]/60" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={T('بحث بالاسم أو الرقم التسلسلي أو الموظف', 'Search name, SN, holder')}
                        className="ps-8 w-full sm:w-[280px] bg-white dark:bg-slate-800 border-[#C4BFF0]"
                        data-testid="assets-search"
                      />
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-[160px] bg-white dark:bg-slate-800 border-[#C4BFF0]" data-testid="filter-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{T('جميع الفئات', 'All categories')}</SelectItem>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.key} value={c.key}>{T(c.ar, c.en)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-[#C4BFF0]" data-testid="filter-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{T('كل الحالات', 'All statuses')}</SelectItem>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.key} value={s.key}>{T(s.ar, s.en)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-14"><SpinnerGap size={28} className="animate-spin text-[#655dc6]" /></div>
                ) : filteredAssets.length === 0 ? (
                  <div className="text-center py-10 text-sm text-[#655dc6]/60" data-testid="assets-empty">
                    {T('لا توجد أصول — ابدأ بإضافة أول صنف', 'No assets yet — add your first one')}
                  </div>
                ) : (
                  <>
                  {/* Mobile Card List (<640px) */}
                  <div className="sm:hidden space-y-3" data-testid="assets-mobile-list">
                    {filteredAssets.map((a) => {
                      const cat = catMeta(a.category);
                      const st = statusMeta(a.status);
                      const Icon = cat.icon;
                      return (
                        <div
                          key={a.id}
                          data-testid={`asset-card-${a.id}`}
                          className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 text-xs text-[#655dc6]/70 mb-1">
                                <Icon size={14} weight="duotone" />
                                {T(cat.ar, cat.en)}
                              </div>
                              <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100">{a.name}</h4>
                              {a.serial_number && (
                                <div className="text-[11px] font-mono text-[#655dc6]/70 mt-0.5">SN: {a.serial_number}</div>
                              )}
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${BADGE_CLASS[st.color]}`}>
                              {T(st.ar, st.en)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            {a.brand && (
                              <div>
                                <span className="block text-[#655dc6]/60 font-medium mb-0.5">{T('الماركة', 'Brand')}</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{a.brand}</span>
                              </div>
                            )}
                            {a.current_holder && (
                              <div className="col-span-2">
                                <span className="block text-[#655dc6]/60 font-medium mb-0.5">{T('مع الموظف', 'Holder')}</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">
                                  {a.current_holder.name} <span className="text-[10px] text-[#655dc6]/60 font-mono">#{a.current_holder.employee_number}</span>
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="pt-3 flex items-center gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                            {a.status === 'available' ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => openHandover(a)}
                                  className="flex-1 min-h-[44px] bg-[#655dc6] hover:bg-[#544cb0] text-white gap-1.5"
                                  data-testid={`handover-btn-mobile-${a.id}`}
                                >
                                  <ArrowCircleUp size={16} weight="bold" />
                                  {T('تسليم', 'Hand over')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditAsset(a)}
                                  className="min-h-[44px] px-3 border-[#C4BFF0] text-[#655dc6]"
                                  data-testid={`edit-asset-mobile-${a.id}`}
                                >
                                  <Pencil size={16} />
                                </Button>
                              </>
                            ) : a.status === 'assigned' ? (
                              <Button
                                size="sm"
                                onClick={() => {
                                  const rec = custody.find((r) => r.id === a.current_custody_id);
                                  if (rec) openReturn(rec);
                                  else toast.error(T('السجل غير موجود', 'Record not found'));
                                }}
                                className="flex-1 min-h-[44px] bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                                data-testid={`return-btn-mobile-${a.id}`}
                              >
                                <ArrowCircleDown size={16} weight="bold" />
                                {T('استرجاع', 'Return')}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteAsset(a)}
                                className="flex-1 min-h-[44px] border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
                                data-testid={`delete-btn-mobile-${a.id}`}
                              >
                                <Trash size={16} />
                                {T('حذف', 'Delete')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop Table (>=640px) */}
                  <div className="hidden sm:block overflow-x-auto">
                    <Table data-testid="assets-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-start">{T('الفئة', 'Category')}</TableHead>
                          <TableHead className="text-start">{T('الاسم', 'Name')}</TableHead>
                          <TableHead className="text-start">{T('الرقم التسلسلي', 'Serial')}</TableHead>
                          <TableHead className="text-start">{T('الماركة', 'Brand')}</TableHead>
                          <TableHead className="text-center">{T('الحالة', 'Status')}</TableHead>
                          <TableHead className="text-start">{T('مع الموظف', 'Holder')}</TableHead>
                          <TableHead className="text-center">{T('إجراء', 'Action')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAssets.map((a) => {
                          const cat = catMeta(a.category);
                          const st = statusMeta(a.status);
                          const Icon = cat.icon;
                          return (
                            <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                              <TableCell>
                                <span className="inline-flex items-center gap-1.5 text-sm">
                                  <Icon size={16} weight="duotone" className="text-[#655dc6]" />
                                  {T(cat.ar, cat.en)}
                                </span>
                              </TableCell>
                              <TableCell className="font-medium">{a.name}</TableCell>
                              <TableCell className="font-mono text-xs">{a.serial_number || '—'}</TableCell>
                              <TableCell className="text-sm">{a.brand || '—'}</TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${BADGE_CLASS[st.color]}`}>
                                  {T(st.ar, st.en)}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm">
                                {a.current_holder ? (
                                  <div>
                                    <div className="font-medium">{a.current_holder.name}</div>
                                    <div className="text-[10px] text-gray-500">#{a.current_holder.employee_number}</div>
                                  </div>
                                ) : <span className="text-gray-400">—</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {a.status === 'available' ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      size="sm"
                                      onClick={() => openHandover(a)}
                                      className="h-8 bg-[#655dc6] hover:bg-[#544cb0] text-white"
                                      data-testid={`handover-btn-${a.id}`}
                                    >
                                      <ArrowCircleUp size={14} weight="bold" className="me-1" />
                                      {T('تسليم', 'Hand over')}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openEditAsset(a)}
                                      className="h-8 px-2 border-[#C4BFF0] text-[#655dc6] hover:bg-[#EDE9FE]"
                                      data-testid={`edit-asset-${a.id}`}
                                      title={T('تعديل الصنف', 'Edit asset')}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                  </div>
                                ) : a.status === 'assigned' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const rec = custody.find((r) => r.id === a.current_custody_id);
                                      if (rec) openReturn(rec);
                                      else toast.error(T('السجل غير موجود', 'Record not found'));
                                    }}
                                    className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid={`return-btn-${a.id}`}
                                  >
                                    <ArrowCircleDown size={14} weight="bold" className="me-1" />
                                    {T('استرجاع', 'Return')}
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="ghost" onClick={() => deleteAsset(a)} className="h-8 text-red-600 hover:bg-red-50" data-testid={`delete-btn-${a.id}`}>
                                    <Trash size={14} />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="custody" className="mt-4">
            <Card className="border-[#C4BFF0] dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-[#655dc6] dark:text-violet-300">{T('سجل العهد', 'Custody ledger')}</CardTitle>
              </CardHeader>
              <CardContent>
                {custody.length === 0 ? (
                  <div className="text-center py-10 text-sm text-[#655dc6]/60">
                    {T('لا توجد عمليات تسليم بعد', 'No handover events yet')}
                  </div>
                ) : (
                  <>
                  {/* Mobile Card List (<640px) */}
                  <div className="sm:hidden space-y-3" data-testid="custody-mobile-list">
                    {custody.map((r) => {
                      const st = r.status === 'active'
                        ? { color: 'violet',  label: isAr ? 'نشطة' : 'Active' }
                        : r.status === 'pending_employee_signature'
                          ? { color: 'amber-wait', label: isAr ? 'بانتظار التوقيع' : 'Pending signature' }
                          : r.status === 'returned'
                            ? { color: 'emerald', label: isAr ? 'مُسترجعة' : 'Returned' }
                            : r.status === 'lost'
                              ? { color: 'red', label: isAr ? 'مفقودة' : 'Lost' }
                              : r.status === 'damaged'
                                ? { color: 'amber', label: isAr ? 'تالفة' : 'Damaged' }
                                : { color: 'violet',  label: r.status };
                      return (
                        <div
                          key={r.id}
                          data-testid={`custody-card-${r.id}`}
                          className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100">{r.user?.name || '—'}</h4>
                              <div className="text-[11px] font-mono text-[#655dc6]/70 mt-0.5">#{r.user?.employee_number}</div>
                              <div className="text-xs text-slate-700 dark:text-slate-300 mt-1.5">
                                <span className="text-[#655dc6]/60">{T('الصنف:', 'Asset:')}</span> {r.asset?.name || '—'}
                                {r.asset?.serial_number && <span className="text-[10px] font-mono text-[#655dc6]/60 ms-1">({r.asset.serial_number})</span>}
                              </div>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${BADGE_CLASS[st.color]}`}>
                              {st.label}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <span className="block text-[#655dc6]/60 font-medium mb-0.5">{T('تاريخ التسليم', 'Handover at')}</span>
                              <span className="font-mono text-slate-800 dark:text-slate-200">{(r.handover_at || '').slice(0, 10) || '—'}</span>
                            </div>
                            <div>
                              <span className="block text-[#655dc6]/60 font-medium mb-0.5">{T('تاريخ الاسترجاع', 'Return at')}</span>
                              <span className="font-mono text-slate-800 dark:text-slate-200">{(r.return_at || '').slice(0, 10) || '—'}</span>
                            </div>
                          </div>

                          <div className="pt-3 flex flex-wrap items-center gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReceipt(r.id, 'handover')}
                              className="min-h-[44px] px-3 border-[#C4BFF0] text-[#655dc6] gap-1"
                              data-testid={`receipt-handover-mobile-${r.id}`}
                            >
                              <FilePdf size={14} /> {T('استلام', 'In')}
                            </Button>
                            {r.return_at && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReceipt(r.id, 'return')}
                                className="min-h-[44px] px-3 border-amber-300 text-amber-700 gap-1"
                                data-testid={`receipt-return-mobile-${r.id}`}
                              >
                                <FilePdf size={14} /> {T('استرجاع', 'Out')}
                              </Button>
                            )}
                            {r.status === 'active' && (
                              <Button
                                size="sm"
                                onClick={() => openReturn(r)}
                                className="min-h-[44px] px-3 bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`return-record-mobile-${r.id}`}
                              >
                                {T('استرجاع', 'Return')}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteTarget(r)}
                              className="min-h-[44px] px-3 border-red-300 text-red-600 hover:bg-red-50 ms-auto"
                              data-testid={`delete-custody-mobile-${r.id}`}
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop Table (>=640px) */}
                  <div className="hidden sm:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-start">{T('الموظف', 'Employee')}</TableHead>
                          <TableHead className="text-start">{T('الصنف', 'Asset')}</TableHead>
                          <TableHead className="text-start">{T('تاريخ التسليم', 'Handover at')}</TableHead>
                          <TableHead className="text-start">{T('تاريخ الاسترجاع', 'Return at')}</TableHead>
                          <TableHead className="text-center">{T('الحالة', 'Status')}</TableHead>
                          <TableHead className="text-center">{T('السند', 'Receipt')}</TableHead>
                          <TableHead className="text-center">{T('إجراء', 'Action')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {custody.map((r) => {
                          const st = r.status === 'active'
                            ? { color: 'violet',  label: isAr ? 'نشطة' : 'Active' }
                            : r.status === 'pending_employee_signature'
                              ? { color: 'amber-wait', label: isAr ? 'بانتظار التوقيع' : 'Pending signature' }
                              : r.status === 'returned'
                                ? { color: 'emerald', label: isAr ? 'مُسترجعة' : 'Returned' }
                                : r.status === 'lost'
                                  ? { color: 'red', label: isAr ? 'مفقودة' : 'Lost' }
                                  : r.status === 'damaged'
                                    ? { color: 'amber', label: isAr ? 'تالفة' : 'Damaged' }
                                    : { color: 'violet',  label: r.status };
                          return (
                            <TableRow key={r.id} data-testid={`custody-row-${r.id}`}>
                              <TableCell>
                                <div className="font-medium">{r.user?.name || '—'}</div>
                                <div className="text-[10px] text-gray-500">#{r.user?.employee_number}</div>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{r.asset?.name || '—'}</div>
                                <div className="text-[10px] font-mono text-gray-500">{r.asset?.serial_number}</div>
                              </TableCell>
                              <TableCell className="text-sm font-mono">{(r.handover_at || '').slice(0, 10)}</TableCell>
                              <TableCell className="text-sm font-mono">{(r.return_at || '—').slice(0, 10)}</TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${BADGE_CLASS[st.color]}`}>
                                  {st.label}
                                </span>
                              </TableCell>
                              <TableCell className="text-center space-x-1 rtl:space-x-reverse">
                                <Button size="sm" variant="outline" onClick={() => openReceipt(r.id, 'handover')} className="h-7 px-2 border-[#C4BFF0] text-[#655dc6]" data-testid={`receipt-handover-${r.id}`}>
                                  <FilePdf size={14} /> {T('استلام', 'In')}
                                </Button>
                                {r.return_at && (
                                  <Button size="sm" variant="outline" onClick={() => openReceipt(r.id, 'return')} className="h-7 px-2 border-amber-300 text-amber-700" data-testid={`receipt-return-${r.id}`}>
                                    <FilePdf size={14} /> {T('استرجاع', 'Out')}
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {r.status === 'active' && (
                                    <Button size="sm" onClick={() => openReturn(r)} className="h-7 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`return-record-${r.id}`}>
                                      {T('استرجاع', 'Return')}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setDeleteTarget(r)}
                                    className="h-7 px-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    data-testid={`delete-custody-${r.id}`}
                                    title={T('حذف السجل', 'Delete record')}
                                  >
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Asset Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent dir={direction} className="max-w-md" data-testid="add-asset-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">{T('إضافة صنف جديد', 'Add new asset')}</DialogTitle>
            <DialogDescription>{T('أدخل بيانات الصنف — سيتم اعتباره "متاح" حتى يتم تسليمه.', 'Enter asset details — it will be marked "available" until handed over.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('الفئة', 'Category')} *</label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger className="mt-1" data-testid="add-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{T(c.ar, c.en)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('الاسم', 'Name')} *</label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1" placeholder={T('مثال: HP EliteBook 840 G8', 'e.g. HP EliteBook 840 G8')} data-testid="add-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('الرقم التسلسلي', 'Serial #')}</label>
                <Input value={addForm.serial_number} onChange={(e) => setAddForm({ ...addForm, serial_number: e.target.value })} className="mt-1 font-mono" data-testid="add-serial-input" />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('الماركة', 'Brand')}</label>
                <Input value={addForm.brand} onChange={(e) => setAddForm({ ...addForm, brand: e.target.value })} className="mt-1" data-testid="add-brand-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('الموديل', 'Model')}</label>
                <Input value={addForm.model} onChange={(e) => setAddForm({ ...addForm, model: e.target.value })} className="mt-1" data-testid="add-model-input" />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('قيمة الشراء (ر.س)', 'Purchase value (SAR)')}</label>
                <Input value={addForm.purchase_value} onChange={(e) => setAddForm({ ...addForm, purchase_value: e.target.value })} className="mt-1 font-mono" type="number" min="0" data-testid="add-value-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)} data-testid="add-cancel-btn">{T('إلغاء', 'Cancel')}</Button>
            <Button onClick={createAsset} disabled={savingAdd} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="add-save-btn">
              {savingAdd ? <SpinnerGap size={16} className="animate-spin me-1" /> : <Plus size={16} className="me-1" />}
              {T('حفظ', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent dir={direction} className="max-w-md" data-testid="edit-asset-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Pencil size={16} />
              {T('تعديل الصنف', 'Edit asset')}
            </DialogTitle>
            <DialogDescription>
              {T('يمكن التعديل فقط عندما يكون الأصل في حالة «متاح».', 'Editing is only allowed while the asset is "Available".')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('الفئة', 'Category')} *</label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{T(c.ar, c.en)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('الاسم', 'Name')} *</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="edit-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('الرقم التسلسلي', 'Serial #')}</label>
                <Input value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} className="mt-1 font-mono" data-testid="edit-serial-input" />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('الماركة', 'Brand')}</label>
                <Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} className="mt-1" data-testid="edit-brand-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('الموديل', 'Model')}</label>
                <Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} className="mt-1" data-testid="edit-model-input" />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('قيمة الشراء (ر.س)', 'Purchase value (SAR)')}</label>
                <Input value={editForm.purchase_value} onChange={(e) => setEditForm({ ...editForm, purchase_value: e.target.value })} className="mt-1 font-mono" type="number" min="0" data-testid="edit-value-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold">{T('ملاحظات', 'Notes')}</label>
              <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1" data-testid="edit-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="edit-cancel-btn">{T('إلغاء', 'Cancel')}</Button>
            <Button onClick={saveEditAsset} disabled={savingEdit} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="edit-save-btn">
              {savingEdit ? <SpinnerGap size={16} className="animate-spin me-1" /> : <Pencil size={16} className="me-1" />}
              {T('حفظ التعديلات', 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handover Dialog */}
      <Dialog open={!!handoverTarget} onOpenChange={(o) => !o && setHandoverTarget(null)}>
        <DialogContent dir={direction} className="max-w-lg" data-testid="handover-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {T(`تسليم: ${handoverTarget?.name || ''}`, `Hand over: ${handoverTarget?.name || ''}`)}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {T(`الرقم التسلسلي: ${handoverTarget?.serial_number || '—'}`, `Serial: ${handoverTarget?.serial_number || '—'}`)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('الموظف المستلم', 'Recipient')} *</label>
              <Select value={handoverForm.user_id} onValueChange={(v) => {
                const emp = employees.find((e) => e.id === v);
                setHandoverForm({ ...handoverForm, user_id: v, signature_name: emp?.name || '' });
              }}>
                <SelectTrigger className="mt-1" data-testid="handover-user-select">
                  <SelectValue placeholder={T('اختر موظفاً', 'Pick employee')} />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.employee_number && `· #${e.employee_number}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('حالة الصنف وقت التسليم', 'Condition at handover')}</label>
              <Select value={handoverForm.condition} onValueChange={(v) => setHandoverForm({ ...handoverForm, condition: v })}>
                <SelectTrigger className="mt-1" data-testid="handover-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">{T('جيدة', 'Good')}</SelectItem>
                  <SelectItem value="fair">{T('متوسطة', 'Fair')}</SelectItem>
                  <SelectItem value="poor">{T('سيئة', 'Poor')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('ملاحظات', 'Notes')}</label>
              <Textarea
                value={handoverForm.notes}
                onChange={(e) => setHandoverForm({ ...handoverForm, notes: e.target.value })}
                className="mt-1" rows={2}
                placeholder={T('ملاحظات تظهر على سند الاستلام (اختياري)', 'Notes appear on the PDF receipt')}
                data-testid="handover-notes"
              />
            </div>
            {/* Mode toggle: request employee signature vs sign now */}
            <div className="rounded-lg border border-[#C4BFF0] bg-[#F8F7FC] p-3">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="handover-mode-toggle-label">
                <input
                  type="checkbox"
                  checked={handoverForm.request_employee_signature}
                  onChange={(e) => setHandoverForm({ ...handoverForm, request_employee_signature: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-[#655dc6]"
                  data-testid="handover-request-signature"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#262263]">
                    {T('إرسال طلب توقيع إلى الموظف', 'Request signature from employee')}
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5 leading-relaxed">
                    {T(
                      'سيصل الموظف إشعار بالتوقيع من لوحته الشخصية. تظهر العهدة لديك بحالة "بانتظار التوقيع" حتى يُوقّع.',
                      'The employee receives an in-app notification to sign from their own dashboard. The record stays in "Pending signature" until signed.',
                    )}
                  </div>
                </div>
              </label>
            </div>
            {!handoverForm.request_employee_signature && (
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('توقيع الموظف الآن', "Capture signature now")} *</label>
                <div className="mt-1 border border-[#C4BFF0] rounded-md bg-white">
                  <SignaturePad
                    ref={sigRefHandover}
                    height={140}
                    onChange={(empty) => setSigEmptyHandover(empty)}
                    data-testid="handover-signature-pad"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-gray-500">{T('يُحفظ التوقيع في سند استلام PDF', 'Signature appears on the PDF receipt')}</span>
                  <button type="button" onClick={() => { sigRefHandover.current?.clear(); setSigEmptyHandover(true); }} className="text-xs text-[#655dc6] hover:underline" data-testid="handover-signature-clear">
                    {T('مسح', 'Clear')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoverTarget(null)} data-testid="handover-cancel">{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={submitHandover}
              disabled={savingHandover}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
              data-testid="handover-submit"
            >
              {savingHandover
                ? <SpinnerGap size={16} className="animate-spin me-1" />
                : <ArrowCircleUp size={16} className="me-1" />}
              {T('تأكيد التسليم', 'Confirm handover')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => !o && setReturnTarget(null)}>
        <DialogContent dir={direction} className="max-w-lg" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {T(`استرجاع: ${returnTarget?.asset?.name || ''}`, `Return: ${returnTarget?.asset?.name || ''}`)}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {T(`من: ${returnTarget?.user?.name || ''}`, `From: ${returnTarget?.user?.name || ''}`)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('الوضع النهائي', 'Final status')}</label>
                <Select value={returnForm.status} onValueChange={(v) => setReturnForm({ ...returnForm, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="return-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="returned">{T('مُسترجعة', 'Returned')}</SelectItem>
                    <SelectItem value="lost">{T('مفقودة', 'Lost')}</SelectItem>
                    <SelectItem value="damaged">{T('تالفة', 'Damaged')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {returnForm.status === 'returned' && (
                <div>
                  <label className="text-xs text-[#4B5563] font-semibold">{T('حالة الصنف الفعلية', 'Actual condition')}</label>
                  <Select value={returnForm.condition} onValueChange={(v) => setReturnForm({ ...returnForm, condition: v })}>
                    <SelectTrigger className="mt-1" data-testid="return-condition-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">{T('جيدة', 'Good')}</SelectItem>
                      <SelectItem value="fair">{T('متوسطة', 'Fair')}</SelectItem>
                      <SelectItem value="poor">{T('سيئة', 'Poor')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-[#4B5563] font-semibold">{T('ملاحظات', 'Notes')}</label>
              <Textarea value={returnForm.notes} onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })} className="mt-1" rows={2} data-testid="return-notes" />
            </div>
            {returnForm.status === 'returned' && (
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{T('توقيع الموظف المُسلِّم', "Signature of employee handing in")} *</label>
                <div className="mt-1 border border-[#C4BFF0] rounded-md bg-white">
                  <SignaturePad
                    ref={sigRefReturn}
                    height={140}
                    onChange={(empty) => setSigEmptyReturn(empty)}
                    data-testid="return-signature-pad"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-gray-500">{T('يُحفظ على سند الاسترجاع PDF', 'Appears on the return PDF')}</span>
                  <button type="button" onClick={() => { sigRefReturn.current?.clear(); setSigEmptyReturn(true); }} className="text-xs text-[#655dc6] hover:underline">
                    {T('مسح', 'Clear')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={submitReturn}
              disabled={savingReturn}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="return-submit"
            >
              {savingReturn
                ? <SpinnerGap size={16} className="animate-spin me-1" />
                : <ArrowCircleDown size={16} className="me-1" />}
              {T('تأكيد الاسترجاع', 'Confirm return')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete custody confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent dir={direction} className="max-w-md" data-testid="delete-custody-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg text-red-600 flex items-center gap-2">
              <Trash size={18} />
              {T('حذف سجل عهدة', 'Delete custody record')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              {T(
                'سيتم حذف هذا السجل نهائياً. إذا كانت العهدة نشطة فسيُعاد الصنف تلقائياً إلى حالة «متوفر».',
                'This record will be permanently deleted. If the custody is still active, the asset is automatically returned to "available".',
              )}
            </p>
            <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 space-y-1">
              <div className="font-medium text-red-800 dark:text-red-200">
                {deleteTarget?.user?.name || '—'}
              </div>
              <div className="text-xs text-red-700 dark:text-red-300">
                {deleteTarget?.asset?.name} {deleteTarget?.asset?.serial_number && `· ${deleteTarget.asset.serial_number}`}
              </div>
              <div className="text-[11px] font-mono text-red-700/80 dark:text-red-300/80">
                {T('تاريخ التسليم', 'Handed over on')}: {(deleteTarget?.handover_at || '').slice(0, 10)}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              data-testid="delete-custody-cancel"
            >
              {T('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={confirmDeleteCustody}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-custody-confirm"
            >
              {deleting
                ? <SpinnerGap size={16} className="animate-spin me-1" />
                : <Trash size={16} className="me-1" />}
              {T('حذف نهائي', 'Delete permanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
